import { apiMerchantPublic } from "@/api";
import { getAccountToken } from "@/lib/account-token";
import { getMerchantId } from "@/lib/company-storage";
import { isAxiosError } from "axios";

import { readCartId } from "./cart";
import { getOrCreateSessionId } from "./session";

const CLAIM_TIMEOUT_MS = 4000;
const RETRY_BASE_MS = 10_000;
const RETRY_CAP_MS = 5 * 60_000;

type ClaimState =
  | { kind: "idle" }
  | { kind: "claimed"; stamp: string }
  | { kind: "rejected"; stamp: string }
  | { kind: "backoff"; stamp: string; failures: number; retryAt: number };

type ClaimFailure = "rejected" | "transient";

let state: ClaimState = { kind: "idle" };
let inflight: Promise<void> | null = null;

/** Asks the backend to attach the logged-in customer to this tab. No-op without a token. Same retry policy as `ensureStorefrontClaim`. */
export function claimStorefrontSession(): void {
  void ensureStorefrontClaim();
}

/**
 * Resolves once the claim for the current identity (tab session + token) is
 * settled or deferred, never rejects. A `4xx` answer ends the attempts for
 * that identity until the page reloads or the token changes; a network
 * error, timeout or `5xx` is retried on later calls with exponential backoff
 * (10s doubling, capped at 5min, jittered). Cheap to call on every flush.
 */
export function ensureStorefrontClaim(): Promise<void> {
  if (inflight) return inflight;
  inflight = attempt().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Tests only: forget every claim outcome of this page load. */
export function resetStorefrontClaimState(): void {
  state = { kind: "idle" };
  inflight = null;
}

async function attempt(): Promise<void> {
  let merchantId: string | null;
  let token: string | null;
  try {
    merchantId = getMerchantId();
    token = getAccountToken();
  } catch {
    return;
  }
  if (!merchantId || !token) return;

  const sessionId = getOrCreateSessionId(merchantId);
  const stamp = `${sessionId}:${token.slice(-16)}`;
  if (!shouldAttempt(stamp, Date.now())) return;

  const cartId = readCartId(merchantId);
  try {
    await apiMerchantPublic.post(
      `/public/merchants/${encodeURIComponent(merchantId)}/storefront/claims`,
      { sessionId, cartId: cartId ?? "" },
      { timeout: CLAIM_TIMEOUT_MS, headers: { Authorization: `Bearer ${token}` } },
    );
    state = { kind: "claimed", stamp };
  } catch (error) {
    state = nextStateAfterFailure(stamp, classifyFailure(error), Date.now());
  }
}

function shouldAttempt(stamp: string, now: number): boolean {
  if (state.kind === "idle" || state.stamp !== stamp) return true;
  if (state.kind === "backoff") return now >= state.retryAt;
  return false;
}

function nextStateAfterFailure(stamp: string, failure: ClaimFailure, now: number): ClaimState {
  if (failure === "rejected") return { kind: "rejected", stamp };
  const failures = state.kind === "backoff" && state.stamp === stamp ? state.failures + 1 : 1;
  return { kind: "backoff", stamp, failures, retryAt: now + backoffDelayMs(failures) };
}

function backoffDelayMs(failures: number): number {
  const exponential = RETRY_BASE_MS * 2 ** (failures - 1);
  const jittered = exponential * (0.5 + Math.random());
  return Math.min(RETRY_CAP_MS, Math.round(jittered));
}

function classifyFailure(error: unknown): ClaimFailure {
  if (!isAxiosError(error)) return "transient";
  const status = error.response?.status;
  if (status !== undefined && status >= 400 && status < 500) return "rejected";
  return "transient";
}
